import React, { useEffect, useState } from 'react';
import { formatCurrency, getErrorMessage } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  History,
  Download,
  Filter,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  ChevronRight,
  Calendar,
  Building2,
  Receipt,
  TrendingUp,
  Loader2,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Transaction {
  id: string;
  session_id?: string;
  buyer_id: string;
  seller_id: string;
  order_ids: string[];
  amount: number;
  currency: string;
  status: string;
  payment_status: string;
  created_at: string;
  processed_at?: string;
  partner_name?: string;
  partner_email?: string;
  order_count: number;
  invoice_number?: string;
  has_invoice?: boolean;
  orders?: any[];
  partner?: {
    name: string;
    email: string;
    vat_number?: string;
  };
  invoice?: any;
}

interface Stats {
  total_paid: number;
  total_pending: number;
  total_failed: number;
  amount_paid: number;
  amount_pending: number;
}

interface Partner {
  id: string;
  name: string;
  email?: string;
}

const TransactionHistoryPage = () => {
  const { isBuyer, isSeller } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<Stats>({
    total_paid: 0,
    total_pending: 0,
    total_failed: 0,
    amount_paid: 0,
    amount_pending: 0
  });
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [partnerFilter, setPartnerFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Detail dialog
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchTransactions();
    fetchPartners();
  }, [statusFilter, partnerFilter, dateFrom, dateTo, page]);

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', String(pageSize));
      params.append('skip', String(page * pageSize));
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (partnerFilter && partnerFilter !== 'all') {
        params.append('partner_id', partnerFilter);
      }
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }

      const res = await axios.get(`${API}/billing/transactions?${params.toString()}`);
      setTransactions(res.data.transactions);
      setTotal(res.data.total);
      setStats(res.data.stats);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Errore nel caricamento delle transazioni');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API}/billing/partners`);
      setPartners(res.data.partners);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const fetchTransactionDetail = async (transactionId: string) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/billing/transactions/${transactionId}`);
      setSelectedTransaction(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const res = await axios.get(`${API}/billing/transactions/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transazioni_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const res = await axios.get(`${API}/billing/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fattura_${invoiceId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Errore nel download della fattura');
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setPartnerFilter('all');
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
    setPage(0);
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; icon: any; label: string }> = {
      paid: { color: 'bg-green-500/10 text-green-600 border-green-500/20', icon: CheckCircle, label: 'Pagato' },
      pending: { color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', icon: Clock, label: 'In attesa' },
      initiated: { color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', icon: Clock, label: 'Iniziato' },
      failed: { color: 'bg-red-500/10 text-red-600 border-red-500/20', icon: XCircle, label: 'Fallito' },
    };
    const { color, icon: Icon, label } = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
        <Icon className="h-3 w-3" />
        {label}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="transaction-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <History className="h-8 w-8" />
            Storico Transazioni
          </h1>
          <p className="text-muted-foreground mt-1">
            {isBuyer ? 'I tuoi pagamenti ai fornitori' : 'Pagamenti ricevuti dai clienti'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtri
          </Button>
          <Button onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />
            Esporta CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pagati</p>
                <p className="text-2xl font-bold text-green-600">{stats.total_paid}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(stats.amount_paid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Attesa</p>
                <p className="text-2xl font-bold text-amber-600">{stats.total_pending}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(stats.amount_pending)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Falliti</p>
                <p className="text-2xl font-bold text-red-600">{stats.total_failed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totale</p>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-xs text-muted-foreground">transazioni</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="paid">Pagati</SelectItem>
                    <SelectItem value="pending">In attesa</SelectItem>
                    <SelectItem value="failed">Falliti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{isBuyer ? 'Fornitore' : 'Cliente'}</Label>
                <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                  <SelectTrigger data-testid="partner-filter">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {partners.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Da</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from"
                />
              </div>
              
              <div className="space-y-2">
                <Label>A</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to"
                />
              </div>
              
              <div className="flex items-end">
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  <X className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Transazioni ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mb-4" />
              <p>Nessuna transazione trovata</p>
              <p className="text-sm">Prova a modificare i filtri</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => {
                    fetchTransactionDetail(tx.id);
                  }}
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Receipt className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{tx.partner_name || 'N/A'}</span>
                        {getStatusBadge(tx.payment_status)}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(tx.created_at)}
                        </span>
                        <span>{tx.order_count} ordini</span>
                        {tx.invoice_number && (
                          <span className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {tx.invoice_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-bold">{formatCurrency(tx.amount)}</span>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <p className="text-sm text-muted-foreground">
                Mostrando {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} di {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Precedente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(page + 1) * pageSize >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Successivo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Dettaglio Transazione
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : selectedTransaction && (
            <div className="space-y-6">
              {/* Status and Amount */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Importo</p>
                  <p className="text-3xl font-bold">{formatCurrency(selectedTransaction.amount)}</p>
                </div>
                {getStatusBadge(selectedTransaction.payment_status)}
              </div>

              {/* Partner Info */}
              {selectedTransaction.partner && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    {isBuyer ? 'Fornitore' : 'Cliente'}
                  </Label>
                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{selectedTransaction.partner.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedTransaction.partner.email}</p>
                      {selectedTransaction.partner.vat_number && (
                        <p className="text-xs text-muted-foreground">P.IVA: {selectedTransaction.partner.vat_number}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Creata</Label>
                  <p className="font-medium">{formatDate(selectedTransaction.created_at)}</p>
                </div>
                {selectedTransaction.processed_at && (
                  <div>
                    <Label className="text-muted-foreground">Processata</Label>
                    <p className="font-medium">{formatDate(selectedTransaction.processed_at)}</p>
                  </div>
                )}
              </div>

              {/* Orders */}
              {selectedTransaction.orders && selectedTransaction.orders.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Ordini inclusi ({selectedTransaction.orders.length})</Label>
                  <div className="space-y-2">
                    {selectedTransaction.orders.map((order: any) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium">#{order.order_number || order.id.slice(0, 8)}</p>
                          <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                        </div>
                        <p className="font-medium">{formatCurrency(order.total)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invoice */}
              {selectedTransaction.invoice && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Fattura</Label>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">{selectedTransaction.invoice.invoice_number}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadInvoice(selectedTransaction.invoice.id)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Scarica PDF
                    </Button>
                  </div>
                </div>
              )}

              {/* Session ID */}
              {selectedTransaction.session_id && (
                <div>
                  <Label className="text-muted-foreground">ID Sessione Stripe</Label>
                  <p className="text-sm font-mono bg-muted/30 p-2 rounded break-all">
                    {selectedTransaction.session_id}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionHistoryPage;
